// NotchedTile — a rounded-rectangle tile with a half-circle notch bitten
// out of one edge (top or bottom). Used in pairs to sandwich a circular
// element (the cognitive-fingerprint orb) so it sits in the seam between
// the two tiles. Measures itself at runtime so the SVG border path traces
// the exact outline, including the concave notch.

import { useEffect, useRef, useState } from 'react';
import type React from 'react';
import { THICK_TILE_STYLE } from './tileStyles';

/** Notch radius — the orb "hole" diameter = 2 × this. */
export const ORB_RADIUS = 170;

/** Outer corner radius — matches rounded-2xl. */
export const TILE_CORNER_R = 16;

interface NotchedTileProps {
  position: 'top' | 'bottom';
  contentClassName?: string;
  children: React.ReactNode;
}

/**
 * Structure:
 *   [wrapper — measured via ResizeObserver]
 *   ├─ [SVG drop-shadow] — three-pass shadow filter behind the tile
 *   ├─ [masked layer] — gradient background + content, clipped to the tile shape
 *   └─ [SVG overlay]  — unmasked, draws the stroke outline along the exact path
 */
export function NotchedTile({ position, contentClassName = '', children }: NotchedTileProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  useEffect(() => {
    if (!wrapperRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setDims({
        w: entry.contentRect.width,
        h: entry.contentRect.height,
      });
    });
    ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, []);

  const R = TILE_CORNER_R;
  const OR = ORB_RADIUS;

  const notchMask =
    position === 'top'
      ? `radial-gradient(circle ${OR}px at 50% 100%, transparent ${OR}px, black ${OR + 1}px)`
      : `radial-gradient(circle ${OR}px at 50% 0%, transparent ${OR}px, black ${OR + 1}px)`;

  // Outline path traversing ALL four rounded corners plus the concave notch.
  // Outer corners use sweep=1 (CW around the rect); the notch uses sweep=0
  // (curves INTO the tile along the upper/lower half of a circle).
  let outlinePath = '';
  if (dims.w > 0 && dims.h > 0) {
    const { w, h } = dims;
    if (position === 'top') {
      outlinePath = [
        `M ${R} 0`,
        `L ${w - R} 0`,
        `A ${R} ${R} 0 0 1 ${w} ${R}`,
        `L ${w} ${h - R}`,
        `A ${R} ${R} 0 0 1 ${w - R} ${h}`,
        `L ${w / 2 + OR} ${h}`,
        `A ${OR} ${OR} 0 0 0 ${w / 2 - OR} ${h}`,
        `L ${R} ${h}`,
        `A ${R} ${R} 0 0 1 0 ${h - R}`,
        `L 0 ${R}`,
        `A ${R} ${R} 0 0 1 ${R} 0`,
        'Z',
      ].join(' ');
    } else {
      outlinePath = [
        `M ${R} 0`,
        `L ${w / 2 - OR} 0`,
        `A ${OR} ${OR} 0 0 0 ${w / 2 + OR} 0`,
        `L ${w - R} 0`,
        `A ${R} ${R} 0 0 1 ${w} ${R}`,
        `L ${w} ${h - R}`,
        `A ${R} ${R} 0 0 1 ${w - R} ${h}`,
        `L ${R} ${h}`,
        `A ${R} ${R} 0 0 1 0 ${h - R}`,
        `L 0 ${R}`,
        `A ${R} ${R} 0 0 1 ${R} 0`,
        'Z',
      ].join(' ');
    }
  }

  const shadowFilterId = `tile-drop-${position}`;

  return (
    <div ref={wrapperRef} className="flex-1 relative">
      {/* Drop-shadow layer (unmasked, sits BEHIND the masked content).
         Three-pass stack mirroring the multi-layer outer drops in
         THICK_TILE_STYLE: a wide atmospheric drop, a tighter mid drop,
         and a contact line. */}
      {outlinePath && (
        <svg
          className="absolute inset-0 pointer-events-none"
          width={dims.w}
          height={dims.h}
          viewBox={`0 0 ${dims.w} ${dims.h}`}
          style={{ overflow: 'visible' }}
        >
          <defs>
            <filter id={`${shadowFilterId}-far`} x="-15%" y="-15%" width="130%" height="155%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="18" />
              <feOffset dx="0" dy="20" />
              <feComponentTransfer>
                <feFuncA type="linear" slope="0.75" />
              </feComponentTransfer>
            </filter>
            <filter id={`${shadowFilterId}-mid`} x="-10%" y="-10%" width="120%" height="140%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="9" />
              <feOffset dx="0" dy="10" />
              <feComponentTransfer>
                <feFuncA type="linear" slope="0.55" />
              </feComponentTransfer>
            </filter>
            <filter id={`${shadowFilterId}-near`} x="-5%" y="-5%" width="110%" height="120%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="3" />
              <feOffset dx="0" dy="3" />
              <feComponentTransfer>
                <feFuncA type="linear" slope="0.60" />
              </feComponentTransfer>
            </filter>
          </defs>
          <path d={outlinePath} fill="black" filter={`url(#${shadowFilterId}-far)`} />
          <path d={outlinePath} fill="black" filter={`url(#${shadowFilterId}-mid)`} />
          <path d={outlinePath} fill="black" filter={`url(#${shadowFilterId}-near)`} />
        </svg>
      )}

      {/* Masked layer: background + content, clipped by the notch mask. */}
      <div
        className="absolute inset-0"
        style={{
          maskImage: notchMask,
          WebkitMaskImage: notchMask,
        }}
      >
        <div className="absolute inset-0 rounded-2xl" style={THICK_TILE_STYLE} />
        <div className={`relative h-full ${contentClassName}`}>{children}</div>
      </div>

      {/* SVG stroke overlay (unmasked) — traces the outline including the notch. */}
      {outlinePath && (
        <svg
          className="absolute inset-0 pointer-events-none"
          width={dims.w}
          height={dims.h}
          viewBox={`0 0 ${dims.w} ${dims.h}`}
        >
          <path
            d={outlinePath}
            fill="none"
            stroke="rgba(255,255,255,0.12)"
            strokeWidth="1"
          />
        </svg>
      )}
    </div>
  );
}
